import { Paging } from 'api/inc-api.types';
import {
  getEmailPulseDataTags,
  getOffersTrendsParams,
  getRecommendationReportParams,
  getSalesByLocationParams,
  transformRecommendationsTopItems,
  transformSalesByLocationData,
} from 'api/reports-service/reports-service.utils';
import { getClientAlias } from 'util/client-alias';
import { getClientId } from 'util/client-id';
import { generateProvidesList } from 'util/generateTags';
import { getMerchantId } from 'util/merchant-id';
import { incApi } from '../inc-api';
import {
  EmailPulseBaseParams,
  EmailPulseBreakdownResponse,
  EmailPulseExportParams,
  EmailPulseDataResponse,
  EmailPulseOverviewSummary,
  EmailPulsePaginationParams,
  EmailPulseTrendsResponse,
  ExportCustomersCSVParams,
  ExportCustomersCSVResponse,
  LPComparisonDateParams,
  LPDateParams,
  LPStoreParams,
  LineChartResponse,
  LocationPerformanceParams,
  LocationTrendsParams,
  OfferTrendsParams,
  GetRecommendationsSummaryResponse,
  GetRecommendationsTopItemsResponse,
  GetRecommendationsAverageBasketValueResponse,
  RecommendationsReportRequestParams,
  RecommendationsSummaryParams,
  GetAvailableOffersResponse,
  GetJobsStatusResponse,
  OfferPerformanceDownloadParams,
  LocationsPerformanceDownloadParams,
  LocationsTrendsDownloadParams,
  LoyaltyReportSummaryCard,
  MenuItem,
  OfferPerformanceData,
  OfferPerformanceParams,
  OfferTrendsDownloadParams,
  PieChartResponse,
  PulseOffersResponse,
  PulseMessagesResponse,
  SalesByLocation,
} from './reports-service.types';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    getSalesByLocation: build.query<
      SalesByLocation,
      { startDate: string; endDate: string; storeIds: string }
    >({
      query: params => {
        return {
          url: `incentivio-reports/clients/${getClientId()}/sales-by-location`,
          params: getSalesByLocationParams(params),
          method: 'GET',
        };
      },
      transformResponse: (res: SalesByLocation) =>
        transformSalesByLocationData(res),
    }),
    listMenuItems: build.query<{ content: MenuItem[] } & Paging, any>({
      query: params => ({
        url: `incentivio-reports/menuintelligence/itemMetrics`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.content,
        element => element.itemName,
        'intelligenceItems',
      ),
      extraOptions: {
        timeout: 100000,
      },
    }),
    getMenuGroups: build.query({
      query: params => ({
        url: `incentivio-reports/menuintelligence/groups`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.groupDetails,
        element => element.groupName,
        'MenuGroup',
      ),
    }),
    getLocations: build.query({
      query: params => ({
        url: `incentivio-reports/menuintelligence/locations`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.locationDetails,
        element => element.locationId,
        'Locations',
      ),
    }),
    exportCsv: build.query({
      query: params => ({
        url: `incentivio-reports/menuintelligence/itemMetrics/export`,
        method: 'GET',
        params,
        responseHandler: response => response.text(),
      }),
    }),

    getGiftCardPoolReport: build.query<{ content }, any>({
      query: params => {
        const startdate = `${params.startdate.toString().slice(0, 19)}Z`;
        const enddate = `${params.enddate.toString().slice(0, 19)}Z`;
        params = {
          ...params,
          startdate,
          enddate,
        };
        return {
          url: `incentivio-reports/clients/${getClientId()}/giftcardpoolsummary`,
          params: { ...params, merchantid: getMerchantId() },
        };
      },
      providesTags: generateProvidesList(
        result => result?.amountByLocationList,
        element => element.locationId,
        'AmountByLocationList',
      ),
      transformResponse: res => ({
        amountByLocationList: res.amountByLocationList,
        ...res,
      }),
    }),

    exportGiftCardPoolReport: build.query({
      query: params => {
        let storeIdParam = params.storeid;
        params = { ...params, storeid: storeIdParam };
        return {
          url: `incentivio-reports/clients/${getClientId()}/giftcardpoolsummary/download-csv`,
          params,
          responseHandler: response => response.text(),
        };
      },
    }),

    exportCustomersCSV: build.mutation<
      ExportCustomersCSVResponse,
      ExportCustomersCSVParams
    >({
      query: ({ email, ...filterParams }) => ({
        url: `incentivio-reports/clients/customers/download-active`,
        method: 'POST',
        body: {
          clientId: getClientId(),
          clientName: getClientAlias(),
          emailIds: [email],
          ...filterParams,
        },
        extraOptions: {
          timeout: 100000,
        },
      }),
    }),

    exportOfferPerformanceCSV: build.mutation({
      query: (params: OfferPerformanceDownloadParams) => ({
        url: 'incentivio-reports/loyaltyreport/offers/performance/csv',
        method: 'POST',
        body: {
          ...params,
          clientName: getClientAlias(),
        },
      }),
    }),

    exportLocationsPerformanceCSV: build.mutation({
      query: (params: LocationsPerformanceDownloadParams) => ({
        url: 'incentivio-reports/loyaltyreport/locations/performance/csv',
        method: 'POST',
        body: {
          ...params,
          clientName: getClientAlias(),
        },
      }),
    }),

    exportOfferTrendsCSV: build.mutation({
      query: (params: OfferTrendsDownloadParams) => ({
        url: 'incentivio-reports/loyaltyreport/offers/trends/csv',
        method: 'POST',
        body: {
          ...params,
          clientName: getClientAlias(),
        },
      }),
    }),

    exportLocationsTrendsCSV: build.mutation({
      query: (params: LocationsTrendsDownloadParams) => ({
        url: 'incentivio-reports/loyaltyreport/locations/trends/csv',
        method: 'POST',
        body: {
          ...params,
          clientName: getClientAlias(),
        },
      }),
    }),

    brandOrders: build.query<PieChartResponse, LPDateParams>({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/brand/orders`,
        method: 'GET',
        params,
      }),
    }),

    brandPointsSummary: build.query<
      LoyaltyReportSummaryCard,
      LPComparisonDateParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/brand/pointssummary`,
        method: 'GET',
        params,
      }),
    }),

    brandSummary: build.query<LoyaltyReportSummaryCard, LPComparisonDateParams>(
      {
        query: params => ({
          url: `incentivio-reports/loyaltyreport/brand/summary`,
          method: 'GET',
          params,
        }),
      },
    ),

    brandSignupAggregate: build.query<PieChartResponse, LPDateParams>({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/brand/signup/aggregate`,
        method: 'GET',
        params,
      }),
    }),

    brandSignupTimeSeries: build.query<LineChartResponse, LPDateParams>({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/brand/signup/timeseries`,
        method: 'GET',
        params,
      }),
    }),

    franchiseActiveMembers: build.query<
      LineChartResponse,
      LPDateParams & LPStoreParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/franchise/activemembers`,
        method: 'GET',
        params,
      }),
    }),

    franchiseLoyaltySales: build.query<
      LineChartResponse,
      LPDateParams & LPStoreParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/franchise/loyaltysales`,
        method: 'GET',
        params,
      }),
    }),

    franchiseSummary: build.query<
      LoyaltyReportSummaryCard,
      LPComparisonDateParams & LPStoreParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/franchise/summary`,
        method: 'GET',
        params,
      }),
    }),

    locationsPerformanceSummary: build.query<
      LoyaltyReportSummaryCard,
      LocationPerformanceParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/locations/performance/summary`,
        method: 'GET',
        params,
      }),
    }),

    locationsPerformanceData: build.query<
      LoyaltyReportSummaryCard,
      LocationPerformanceParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/locations/performance/data`,
        method: 'GET',
        params,
      }),
    }),

    locationsTrends: build.query<
      LoyaltyReportSummaryCard,
      LocationTrendsParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/locations/trends`,
        method: 'GET',
        params,
      }),
    }),

    offersPerformanceSummary: build.query<
      LoyaltyReportSummaryCard,
      OfferPerformanceParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/offers/performance/summary`,
        method: 'GET',
        params,
      }),
    }),

    offersPerformanceData: build.query<
      OfferPerformanceData,
      OfferPerformanceParams
    >({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/offers/performance/data`,
        method: 'GET',
        params,
      }),
    }),

    offersTrends: build.query<LineChartResponse, OfferTrendsParams>({
      query: params => ({
        url: `incentivio-reports/loyaltyreport/offers/trends`,
        method: 'GET',
        params: getOffersTrendsParams(params),
      }),
    }),

    // Email Pulse Consolidated Endpoints
    getEmailPulseSummary: build.query<
      EmailPulseOverviewSummary,
      EmailPulseBaseParams
    >({
      query: ({ reportType, ...params }) => ({
        url: `incentivio-reports/pulse/email/${reportType}/cards`,
        method: 'GET',
        params,
      }),
    }),

    getEmailPulseEmailsOpenedBreakdown: build.query<
      EmailPulseBreakdownResponse,
      EmailPulseBaseParams
    >({
      query: ({ reportType, ...params }) => ({
        url: `incentivio-reports/pulse/email/${reportType}/opened`,
        method: 'GET',
        params,
      }),
    }),

    getEmailPulseEmailsBouncedBreakdown: build.query<
      EmailPulseBreakdownResponse,
      EmailPulseBaseParams
    >({
      query: ({ reportType, ...params }) => ({
        url: `incentivio-reports/pulse/email/${reportType}/bounced`,
        method: 'GET',
        params,
      }),
    }),

    getEmailPulseTrends: build.query<
      EmailPulseTrendsResponse,
      EmailPulseBaseParams
    >({
      query: params => ({
        url: `incentivio-reports/pulse/email/overview/trends`,
        method: 'GET',
        params,
      }),
    }),

    getEmailPulseData: build.query<
      EmailPulseDataResponse,
      EmailPulseBaseParams & EmailPulsePaginationParams
    >({
      query: ({ reportType, ...params }) => ({
        url: `incentivio-reports/pulse/email/${reportType}/data`,
        method: 'GET',
        params,
      }),
      providesTags: (result, _error, { reportType }) =>
        getEmailPulseDataTags(result, reportType),
    }),

    exportEmailPulse: build.mutation<
      any,
      EmailPulseExportParams & { reportType: string }
    >({
      query: ({ reportType, ...params }) => ({
        url: `incentivio-reports/pulse/email/${reportType}/data/csv`,
        method: 'POST',
        body: {
          ...params,
          clientname: getClientAlias(),
        },
      }),
    }),

    getPulseOffers: build.query<PulseOffersResponse, void>({
      query: () => ({
        url: 'incentivio-reports/pulse/email/offers',
        method: 'GET',
      }),
    }),

    getPulseMessages: build.query<PulseMessagesResponse, void>({
      query: () => ({
        url: 'incentivio-reports/pulse/email/messages',
        method: 'GET',
      }),
    }),

    getRecommendationsSummary: build.query<
      GetRecommendationsSummaryResponse,
      RecommendationsSummaryParams
    >({
      query: params => ({
        url: `incentivio-reports/clients/${getClientId()}/recommendation-dashboard/summary`,
        method: 'GET',
        params,
      }),
    }),

    getRecommendationsTopItems: build.query<
      GetRecommendationsTopItemsResponse,
      RecommendationsReportRequestParams
    >({
      query: params => ({
        url: `incentivio-reports/clients/${getClientId()}/recommendation-dashboard/top-items`,
        method: 'GET',
        params: getRecommendationReportParams(params),
      }),
      transformResponse: (
        data: GetRecommendationsTopItemsResponse,
        _meta,
        arg,
      ) => transformRecommendationsTopItems(data, arg),
    }),

    getRecommendationsAverageBasketValue: build.query<
      GetRecommendationsAverageBasketValueResponse,
      RecommendationsReportRequestParams
    >({
      query: params => ({
        url: `incentivio-reports/clients/${getClientId()}/recommendation-dashboard/average-basket-value`,
        method: 'GET',
        params: getRecommendationReportParams(params),
      }),
      transformResponse: (
        response: GetRecommendationsAverageBasketValueResponse,
      ) => response.sort((a, b) => a.date - b.date),
    }),

    // Also carries the Guest Journey row's readiness + settings-processing
    // flags, so a Guest Journey settings save/reset must refetch it.
    getJobsStatus: build.query<GetJobsStatusResponse, void>({
      query: () => ({
        url: 'incentivio-reports/jobs/status',
        method: 'GET',
      }),
      providesTags: ['GuestJourneyReport'],
    }),

    getAvailableOffers: build.query<GetAvailableOffersResponse, void>({
      query: () => ({
        url: 'incentivio-reports/offers/available',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetSalesByLocationQuery,
  useListMenuItemsQuery,
  useGetMenuGroupsQuery,
  useLazyGetMenuGroupsQuery,
  useLazyGetLocationsQuery,
  useGetLocationsQuery,
  useLazyExportCsvQuery,
  useGetGiftCardPoolReportQuery,
  useLazyExportGiftCardPoolReportQuery,
  useExportCustomersCSVMutation,
  useExportOfferPerformanceCSVMutation,
  useExportLocationsPerformanceCSVMutation,
  useExportOfferTrendsCSVMutation,
  useExportLocationsTrendsCSVMutation,
  useBrandOrdersQuery,
  useBrandPointsSummaryQuery,
  useBrandSummaryQuery,
  useBrandSignupAggregateQuery,
  useBrandSignupTimeSeriesQuery,
  useFranchiseActiveMembersQuery,
  useFranchiseSummaryQuery,
  useFranchiseLoyaltySalesQuery,
  useLocationsPerformanceSummaryQuery,
  useLocationsPerformanceDataQuery,
  useLocationsTrendsQuery,
  useOffersPerformanceDataQuery,
  useOffersPerformanceSummaryQuery,
  useOffersTrendsQuery,
  useGetEmailPulseSummaryQuery,
  useGetEmailPulseEmailsOpenedBreakdownQuery,
  useGetEmailPulseEmailsBouncedBreakdownQuery,
  useGetEmailPulseTrendsQuery,
  useGetEmailPulseDataQuery,
  useExportEmailPulseMutation,
  useGetPulseOffersQuery,
  useLazyGetPulseOffersQuery,
  useGetPulseMessagesQuery,
  useLazyGetPulseMessagesQuery,
  useGetRecommendationsSummaryQuery,
  useGetRecommendationsTopItemsQuery,
  useGetRecommendationsAverageBasketValueQuery,
  useGetJobsStatusQuery,
  useGetAvailableOffersQuery,
  useLazyGetAvailableOffersQuery,
} = endpoints;
